import { useEffect } from 'react';
import { Loader2, Save, RotateCcw } from 'lucide-react';

/**
 * Floating save bar for admin editors.
 *
 * Long admin forms scroll well past their Save button, so it is easy to change
 * something, wander off, and lose it. This slides up the moment anything is
 * dirty, follows the page, and takes Ctrl/Cmd+S. It also guards the browser's
 * own navigation, because a half-edited form is worth more than a tidy tab
 * close.
 */
export function UnsavedChangesBar({
  dirty,
  saving = false,
  onSave,
  onDiscard,
  label = 'Unsaved changes',
  savingLabel = 'Saving…',
  disabled = false,
  disabledReason,
}: {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  /** Omit to hide the discard button when there is nothing to revert to. */
  onDiscard?: () => void;
  label?: string;
  savingLabel?: string;
  /** Blocks saving — e.g. a required field is still empty. */
  disabled?: boolean;
  disabledReason?: string;
}) {
  // Ctrl/Cmd+S saves instead of opening the browser's save dialog. Bound
  // whenever there is something to save so it works wherever focus happens to
  // be — including inside a text field, which is where someone finishing an
  // edit usually is.
  useEffect(() => {
    if (!dirty) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 's' && event.key !== 'S') return;
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      if (!saving && !disabled) onSave();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, saving, disabled, onSave]);

  // Native "leave site?" prompt. Browsers ignore custom text, so the point is
  // only to make an accidental tab close survivable.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  if (!dirty) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-4">
      <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-zinc-700 bg-zinc-900/95 px-4 py-3 shadow-2xl backdrop-blur">
        <span className="text-sm text-zinc-300">{saving ? savingLabel : label}</span>

        {onDiscard && !saving && (
          <button
            type="button"
            onClick={onDiscard}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-white"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Discard
          </button>
        )}

        <button
          type="button"
          onClick={onSave}
          disabled={saving || disabled}
          title={disabled ? disabledReason : 'Ctrl+S'}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}
